import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Briefcase,
  Heart,
  FileText,
  MessageSquare,
  FolderOpen,
  Users,
  UserCheck,
  Shield,
  Star,
  Upload,
  Calendar,
  Monitor,
  Crown,
  Award,
  User,
  Sparkles,
  Calculator,
} from "lucide-react";
import logoLight from "@/assets/logo-light.png";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSubscription } from "@/hooks/useSubscription";

interface BuyerSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  favoritesCount?: number;
}

const translations = {
  en: {
    dealPlatform: "Deal Platform",
    overview: "Overview",
    allDeals: "All Deals",
    favorites: "Favorites",
    ndaStatus: "NDA Status",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendar",
    consultants: "My Consultants",
    partners: "My Partners",
    experts: "Our Experts",
    submitDeal: "Submit Deal",
    profile: "My Profile",
    upgrade: "Upgrade Plan",
    external: "External",
    listings: "Listings",
    myConsultants: "My Consultants",
    financingCalc: "Financing Calculator",
    plan: "Plan",
  },
  fr: {
    dealPlatform: "Plateforme de transactions",
    overview: "Aperçu",
    allDeals: "Toutes les offres",
    favorites: "Favoris",
    ndaStatus: "Statut NDA",
    messages: "Messages",
    documents: "Documents",
    calendar: "Calendrier",
    consultants: "Mes Consultants",
    partners: "Mes Partenaires",
    experts: "Nos Experts",
    submitDeal: "Soumettre une offre",
    profile: "Mon profil",
    upgrade: "Améliorer le forfait",
    external: "Externe",
    listings: "Annonces",
    myConsultants: "Mes Consultants",
    financingCalc: "Calculateur de Financement",
    plan: "Forfait",
  },
};

const TIER_LABELS: Record<string, { en: string; fr: string; color: string }> = {
  visitor: { en: "Visitor", fr: "Visiteur", color: "bg-muted text-muted-foreground" },
  essential: { en: "Essential", fr: "Essentiel", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  "essential-plus": { en: "Essential Plus", fr: "Essentiel Plus", color: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200" },
  "acquisition-pro": { en: "Acquisition Pro", fr: "Acquisition Pro", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  acquisition_pro: { en: "Acquisition Pro", fr: "Acquisition Pro", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  pro: { en: "Pro", fr: "Pro", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  byod: { en: "BYOD", fr: "BYOD", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
};

interface NavigationItem {
  name: string;
  tab: string;
  icon: React.ElementType;
}

export const BuyerSidebar = ({ activeTab, onTabChange, favoritesCount = 0 }: BuyerSidebarProps) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { tier } = useSubscription();
  const t = translations[language as keyof typeof translations] || translations.en;

  const tierInfo = tier ? TIER_LABELS[tier] : null;
  const tierLabel = tierInfo ? tierInfo[language as 'en' | 'fr'] || tierInfo.en : null;

  const navigation: NavigationItem[] = [
    { name: t.overview, tab: "overview", icon: LayoutDashboard },
    { name: t.allDeals, tab: "deals", icon: Briefcase },
    { name: t.favorites, tab: "favorites", icon: Heart },
    { name: t.ndaStatus, tab: "nda", icon: Shield },
    { name: t.messages, tab: "messages", icon: MessageSquare },
    { name: t.documents, tab: "documents", icon: FolderOpen },
    { name: t.calendar, tab: "calendar", icon: Calendar },
    { name: t.consultants, tab: "consultants", icon: UserCheck },
    { name: t.partners, tab: "partners", icon: Users },
    { name: t.experts, tab: "experts", icon: FileText },
    { name: t.financingCalc, tab: "financing-calc", icon: Calculator },
    { name: t.submitDeal, tab: "submit-deal", icon: Upload },
    { name: t.profile, tab: "profile", icon: User },
  ];

  const externalLinks = [
    { href: "/listings", name: t.listings, icon: Monitor },
    { href: "/buyer/upgrade", name: t.upgrade, icon: Sparkles },
  ];

  return (
    <aside className="w-56 flex flex-col flex-shrink-0" style={{ background: "#1D314D" }}>
      <div className="px-5 pt-5 pb-4">
        <div className="h-px mb-4 rounded" style={{ background: "linear-gradient(90deg, #CEAB65 0%, rgba(206,171,101,0.1) 100%)" }} />
        <img src="/noBgColor.png" alt="AcquizGroup" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        <p className="text-[9px] tracking-widest uppercase mt-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>{t.dealPlatform}</p>
        <div className="h-px mt-3" style={{ background: "linear-gradient(90deg, rgba(206,171,101,0.35) 0%, transparent 80%)" }} />
        {tierLabel && (
          <div className="mt-2 flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Crown className="h-3.5 w-3.5 text-amber-500" />
              <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full", tierInfo?.color)}>
                {tierLabel}
              </span>
            </div>
            {tier === 'essential-plus' && (
              <div className="flex items-center gap-1.5 ml-0.5">
                <Shield className="h-3.5 w-3.5 text-yellow-500" />
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-900 dark:from-amber-900 dark:to-yellow-900 dark:text-amber-100 border border-amber-300 dark:border-amber-700">
                  ✦ Elite Buyer
                </span>
              </div>
            )}
            {(tier === 'acquisition-pro' || tier === 'acquisition_pro') && (
              <div className="flex items-center gap-1.5 ml-0.5">
                <Star className="h-3.5 w-3.5 text-yellow-500" />
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-900 dark:from-purple-900 dark:to-indigo-900 dark:text-purple-100 border border-purple-300 dark:border-purple-700">
                  ✦ Acquisition Pro
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = activeTab === item.tab;
          const Icon = item.icon;

          return (
            <button
              key={item.tab}
              onClick={() => onTabChange(item.tab)}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left",
                isActive
  ? "bg-[#CEAB65]/10 text-[#CEAB65] font-medium"
  : "text-white/55 hover:bg-white/6 hover:text-white/85",
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="flex-1">{item.name}</span>
              {item.tab === 'favorites' && favoritesCount > 0 && (
                <span className={cn(
                  "ml-auto text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5",
                  isActive
                    ? "bg-primary-foreground text-primary"
                    : "bg-red-500 text-white"
                )}>
                  {favoritesCount}
                </span>
              )}
            </button>
          );
        })}

        {/* External Links Section */}
        <div className="pt-4 mt-4" style={{ borderTop: "0.5px solid rgba(255,255,255,0.1)" }}>
  <p className="text-[8.5px] tracking-widest uppercase px-2 py-2" style={{ color: "rgba(255,255,255,0.25)" }}>
            {t.external}
          </p>
          {externalLinks.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left text-white/55 hover:bg-white/6 hover:text-white/85"
              >
                <Icon className="h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </div>
      </nav>

      {user && (
        <div className="px-4 py-3" style={{ borderTop: "0.5px solid rgba(255,255,255,0.07)" }}>
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
         style={{ background: "rgba(206,171,101,0.15)", border: "1px solid rgba(206,171,101,0.25)" }}>
      <Users className="h-4 w-4" style={{ color: "#CEAB65" }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-medium truncate" style={{ color: "rgba(255,255,255,0.75)" }}>{user.email}</p>
      <p className="text-[9px]" style={{ color: "rgba(255,255,255,0.35)" }}>Buyer</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
